// Polls GET /laser/{task_id}/camera while a Laser task is open (laserCamera.js has the rules).
//
// - Reads at once, then every CAMERA_POLL_MS (5 s). Never two requests at once.
// - No timer while the tab is hidden; a read as soon as it is shown again.
// - 400 / 404 / 405 (no endpoint, not a Laser task, unknown task): polling stops, nothing is shown.
// - A failed read (401, 429, 5xx, no answer) is silent: the last answer stays, and goes after
//   CAMERA_FAILS_BEFORE_CLEAR failures in a row (never an old sentence for long).
// - A new task_id starts from nothing; an answer for the task the screen left is dropped.
// - An unchanged answer re-renders nothing.
import { useEffect, useState } from 'react';
import { CAMERA_POLL_MS, CAMERA_FAILS_BEFORE_CLEAR, loadLaserCamera, sameCameraAnswer } from './laserCamera';

export default function useLaserCamera(taskId, active, { pollMs = CAMERA_POLL_MS } = {}) {
  const [camera, setCamera] = useState(null);

  useEffect(() => {
    setCamera(null);
    if (!active || taskId === null || taskId === undefined || taskId === '') return undefined;
    let stopped = false;      // unmounted / task changed
    let gone = false;         // the API has nothing to poll here
    let timer = null;
    let inFlight = false;
    let failures = 0;
    const hidden = () => typeof document !== 'undefined' && document.hidden;

    const schedule = () => {
      if (stopped || gone) return;
      timer = setTimeout(read, pollMs);
    };
    async function read() {
      timer = null;
      if (stopped || gone || inFlight) return;
      if (hidden()) return;                     // resumes on visibilitychange
      inFlight = true;
      const r = await loadLaserCamera(taskId);
      inFlight = false;
      if (stopped) return;
      if (r.missing) {
        gone = true;
        setCamera(null);
        return;
      }
      if (r.camera) {
        failures = 0;
        setCamera((prev) => (sameCameraAnswer(prev, r.camera) ? prev : r.camera));
      } else {
        failures += 1;
        if (failures >= CAMERA_FAILS_BEFORE_CLEAR) setCamera(null);
      }
      schedule();
    }
    const onVisibility = () => {
      if (!hidden() && !timer && !inFlight && !stopped && !gone) read();
    };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
    read();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [taskId, active, pollMs]);

  return camera;
}
